"use client";

import { ClipboardList, Scissors, FlaskConical, Package, Truck } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function ClaimsProcess() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const {
    ref: diagramRef,
    isVisible: diagramVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const {
    ref: footnoteRef,
    isVisible: footnoteVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const steps = [{
    number: "01",
    icon: ClipboardList,
    title: "Report Incident",
    description: "Submit your claim details through our secure portal. Our digital intake system logs your request for immediate review."
  }, {
    number: "02",
    icon: Scissors,
    title: "Verify Coverage",
    description: "Our adjusters cross-reference your policy details. We ensure every aspect of your protection plan is accurately applied."
  }, {
    number: "03",
    icon: FlaskConical,
    title: "Damage Assessment",
    description: "Expert analysis of the reported loss. We utilize advanced diagnostic tools to determine the scope of your claim."
  }, {
    number: "04",
    icon: Package,
    title: "Resolution Plan",
    description: "A tailored recovery strategy is developed. We coordinate with trusted partners to restore your assets efficiently."
  }, {
    number: "05",
    icon: Truck,
    title: "Final Settlement",
    description: "Prompt disbursement of funds or service completion. Vanguard Shield ensures your peace of mind is fully restored."
  }];
  return <section data-section-id="2919" id="claims-process" className="bg-background text-foreground py-24 md:py-32 lg:py-40 px-6 md:px-10 lg:px-16 overflow-hidden">
      <style>{`
        @keyframes travelDot {
          0% { left: 0%; opacity: 0; }
          10% { opacity: 1; }
          90% { opacity: 1; }
          100% { left: 100%; opacity: 0; }
        }
        @keyframes travelDotVertical {
          0% { top: 0%; opacity: 0; }
          10% { opacity: 1; }
          90% { opacity: 1; }
          100% { top: 100%; opacity: 0; }
        }
        .travel-dot-h {
          animation: travelDot 4s linear infinite;
        }
        .travel-dot-v {
          animation: travelDotVertical 4s linear infinite;
        }
      `}</style>

      <div className="max-w-7xl mx-auto">
        {}
        <div ref={headerRef} className={`mb-20 md:mb-28 transition-all duration-1000 ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <div className="flex items-center gap-3 mb-8">
            <span className="font-mono text-xs tracking-widest uppercase text-[var(--chart-1)]">
              [SEC_01]
            </span>
            <span className="h-px w-12 bg-[var(--chart-1)]" />
            <span className="font-mono text-xs tracking-widest uppercase text-muted-foreground">
              The Claims Process
            </span>
          </div>
          <h2 className="font-serif font-light text-4xl md:text-6xl lg:text-7xl leading-[1.05] tracking-tight">
            <span className="block">From incident</span>
            <span className="block">/ to resolution</span>
            <span className="block">
              / with <em className="italic font-normal text-[var(--chart-1)]">absolute certainty</em>.
            </span>
          </h2>
        </div>

        {}
        <div ref={diagramRef} className={`relative transition-all duration-1000 delay-200 ${diagramVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          {}
          <div className="hidden md:block absolute top-[88px] left-[10%] right-[10%] h-px">
            <div className="absolute inset-0 bg-[var(--chart-1)]/30" />
            <div className="travel-dot-h absolute top-1/2 -translate-y-1/2 w-2 h-2 rounded-lg bg-[var(--chart-1)]" style={{
            boxShadow: "0 0 12px hsl(155 85% 35% / 0.8), 0 0 24px hsl(155 85% 35% / 0.4)"
          }} />
          </div>

          {}
          <div className="grid grid-cols-1 md:grid-cols-5 gap-12 md:gap-4 lg:gap-6 relative">
            {}
            <div className="md:hidden absolute left-6 top-8 bottom-8 w-px">
              <div className="absolute inset-0 bg-[var(--chart-1)]/30" />
              <div className="travel-dot-v absolute left-1/2 -translate-x-1/2 w-2 h-2 rounded-lg bg-[var(--chart-1)]" style={{
              boxShadow: "0 0 12px hsl(155 85% 35% / 0.8), 0 0 24px hsl(155 85% 35% / 0.4)"
            }} />
            </div>

            {steps.map((step, i) => {
            const Icon = step.icon;
            return <div data-index={i} key={i} className="flex md:flex-col md:items-center md:text-center gap-6 md:gap-0 pl-16 md:pl-0 relative">
                  {}
                  <div className="md:mb-6 absolute md:relative left-0 md:left-auto top-0 md:top-auto">
                    <span className="font-mono text-2xl md:text-3xl lg:text-4xl font-light text-[var(--chart-1)] tracking-tight">
                      {step.number}
                    </span>
                  </div>

                  {}
                  <div className="hidden md:flex items-center justify-center mb-6 relative z-10">
                    <div className="w-4 h-4 rounded-lg bg-background border-2 border-[var(--chart-1)]" />
                  </div>

                  {}
                  <div className="md:hidden absolute left-[18px] top-2 w-3 h-3 rounded-lg bg-background border-2 border-[var(--chart-1)] z-10" />

                  {}
                  <div className="md:mb-5 flex-shrink-0">
                    <Icon className="w-8 h-8 md:w-10 md:h-10 text-foreground/80" strokeWidth={1} />
                  </div>

                  <div className="flex-1 md:flex-initial">
                    {}
                    <h3 className="font-serif font-default text-xl md:text-2xl font-normal mb-3 md:mb-4 leading-tight">
                      {step.title}
                    </h3>

                    {}
                    <p className="text-sm leading-relaxed text-muted-foreground md:px-2">
                      {step.description}
                    </p>
                  </div>
                </div>;
          })}
          </div>
        </div>

        {}
        <div ref={footnoteRef} className={`mt-20 md:mt-28 transition-all duration-1000 delay-300 ${footnoteVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <div className="border border-border rounded-md p-6 md:p-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-4 bg-card/30">
            <div className="flex items-center gap-3">
              <div className="w-2 h-2 rounded-lg bg-[var(--chart-1)] animate-pulse" />
              <span className="font-mono text-xs tracking-widest uppercase text-muted-foreground">
                Live Claims Status
              </span>
            </div>
            <div className="font-mono text-sm md:text-base tracking-wider text-foreground">
              <span className="text-muted-foreground">AVG:</span> 24HR RESPONSE
              <span className="mx-3 text-[var(--chart-1)]">·</span>
              <span className="text-muted-foreground">STATUS:</span> SECURE
            </div>
          </div>
        </div>
      </div>
    </section>;
}